import type { ReactNode } from "react";
import { DateOfBirthInput } from "../features/self-service/components/DateOfBirthInput";
import { KioskFieldFrame } from "./kiosk-field";

/**
 * Props of {@link KioskDateOfBirthField}.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * type Props = KioskDateOfBirthFieldProps;
 * ```
 */
export interface KioskDateOfBirthFieldProps {
  readonly id: string;
  readonly label: ReactNode;
  readonly testId: string;
  /** `data-testid` of the calendar button; defaults to `${testId}-picker-btn`. */
  readonly pickerTestId?: string;
  /** Birth date as ISO `YYYY-MM-DD` (or the partially typed `TT.MM.JJJJ`). */
  readonly value: string;
  /** Receives the typed text while editing and the ISO date on blur/pick. */
  readonly onChange: (value: string) => void;
  readonly onBlur?: () => void;
  readonly required?: boolean;
  readonly disabled?: boolean;
  /** Translated error message; sets `aria-invalid` and links the message. */
  readonly error?: ReactNode;
  readonly errorTestId?: string;
  /** Format hint shown below the field (e.g. "Format: TT.MM.JJJJ"). */
  readonly description?: ReactNode;
}

/**
 * Kiosk date-of-birth field: {@link DateOfBirthInput} (cake icon, typed
 * `TT.MM.JJJJ` entry, calendar button inside the 56px group) with a floating
 * label and the shared description/error slot. Accepts ISO dates and ISO
 * midnight timestamps and always displays `TT.MM.JJJJ` (KC-74).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <form.Field name="dob">
 *   {(field) => (
 *     <KioskDateOfBirthField
 *       id="checkin-lookup-dob"
 *       testId="frontdesk-patient-lookup-dob-input"
 *       label={t("self-service.check-in.patient-lookup.dob")}
 *       required
 *       value={field.state.value}
 *       onChange={field.handleChange}
 *       onBlur={field.handleBlur}
 *       error={field.state.meta.errors.length > 0 ? t(getFormErrorMessage(field.state.meta.errors[0])) : undefined}
 *     />
 *   )}
 * </form.Field>
 * ```
 */
export function KioskDateOfBirthField({
  id,
  label,
  testId,
  pickerTestId,
  value,
  onChange,
  onBlur,
  required,
  disabled,
  error,
  errorTestId,
  description,
}: KioskDateOfBirthFieldProps) {
  const hasError = Boolean(error);
  const describedBy =
    [description ? `${id}-description` : null, hasError ? `${id}-error` : null]
      .filter((entry) => entry !== null)
      .join(" ") || undefined;

  return (
    <KioskFieldFrame
      id={id}
      testId={testId}
      description={description}
      error={error}
      errorTestId={errorTestId}
    >
      <DateOfBirthInput
        id={id}
        dataTestId={testId}
        pickerDataTestId={pickerTestId}
        floatingLabel={label}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        aria-invalid={hasError}
        aria-describedby={describedBy}
      />
    </KioskFieldFrame>
  );
}
